import { Inject, Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormArray, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { ClientEnvironment, ENVIRONMENT, FormControlModel, StepperGroupModel, emailValidator } from '@thehubz/client/core';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, BuyoutRightsMakeOS, BuyoutRightsMakeOSSecondLevel } from '@thehubz/shared/model';
import {
  buyoutRightsField,
  buyoutRightsFieldMakeOS,
  buyoutRightsFieldSeat,
  buyoutRightsFields,
  buyoutRightsFieldsMakeOS,
  buyoutRightsFieldsSeat,
  buyoutRightsSecondLevelField,
} from '../asset-upload/stepper-config/asset-settings-stepper.constants';
import { duplicateSelectionValidator } from './asset-settings-stepper.form-validators';

@Injectable({
  providedIn: 'root',
})
export class AssetStepperDialogFormService {
  brandingKey!: CustomClients;
  secondLevelLabel!: string;
  secondLevelOptions!: { _id: string; name: string; firstLvl: string }[];

  constructor(@Inject(ENVIRONMENT) env: ClientEnvironment) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
  }
  /**
   * Creates a group of form controls based on form fields of the given step
   * @param step
   * @returns FormGroup, chips: string[]
   */
  getFormGroupFor(step: StepperGroupModel, formFieldVisibility?: { [key: string]: boolean[] }): [FormGroup, string[]] {
    const formGroup: FormGroup = new FormGroup({});
    let chips: string[] = [];

    step.formFields?.forEach((formField) => {
      const fieldObject = formField;
      // create form control and add it to the form group
      let fieldValueObject = [];
      if (fieldObject.formFieldName && fieldObject.formFieldName !== '') {
        const fieldName = formField.formFieldName;
        // handle checkbox
        if (fieldObject.fieldType === 'checkbox') {
          if ((fieldObject.value || 'value' in fieldObject) && !fieldObject.options) {
            fieldValueObject = fieldObject.value;
            const control: FormControl = this.createFormFieldControl(fieldObject);
            formGroup.addControl(fieldName, control);
          }
          if (fieldObject.value && fieldObject.options) {
            const control: FormArray = this.buildCheckboxArray(fieldObject, fieldValueObject);
            formGroup.addControl(fieldName, control);
          }
        } else if (fieldObject['fieldType'] == 'formArray') {
          const control: FormArray = this.buildFormArray(fieldObject.formFieldGroup, formFieldVisibility, step.noValidationFlag);
          formGroup.addControl(fieldName, control);
        } else {
          if (fieldObject.fieldType === 'matChipList') {
            chips = fieldObject.value || [];
          }
          const control: FormControl = this.createFormFieldControl(fieldObject);
          if (fieldObject.disable) {
            control.disable();
          }
          formGroup.addControl(fieldName, control);
        }
      }
    });

    return [formGroup, chips];
  }

  /**
   * Build form array
   * @param formArray
   * @returns
   */
  buildFormArray(formArray: AnyType, formFieldVisibility?: { [key: string]: boolean[] }, noValidationFlag?: boolean): UntypedFormArray {
    const control = new UntypedFormArray([]);
    const groupControl = this.buildFormGroup(formArray, formFieldVisibility);
    control.push(groupControl);
    if (!noValidationFlag) {
      const fieldTypeToExclude = ['checkbox', 'date'];
      const excludeProps =
        formArray.filter((arr: AnyType) => fieldTypeToExclude.includes(arr.fieldType)).map((exclProp: AnyType) => exclProp.formFieldName) || [];
      control.addValidators(duplicateSelectionValidator(excludeProps));
    }
    return control;
  }

  buildFormGroup(formArray: AnyType, formFieldVisibility?: { [key: string]: boolean[] }): FormGroup<AnyType> {
    const groupControl: FormGroup = new FormGroup({});
    formArray.forEach((formField: AnyType) => {
      const fieldName = formField['formFieldName'];
      if (formField.fieldType === 'formArray') {
        const control: FormArray = this.buildFormArray(formField.formFieldGroup, formFieldVisibility);
        groupControl.addControl(fieldName, control);
        return;
      }
      const control: FormControl = this.createFormFieldControl(formField);
      control.setValue(formField.value !== undefined ? formField.value : '');

      if (formFieldVisibility) formFieldVisibility[formField.formFieldName] = [formField.visible];
      if (formField['disable']) {
        control.disable();
      }
      groupControl.addControl(fieldName, control);
    });
    return groupControl;
  }

  /**
   * For creating form control from FormControlModel
   * @param fieldObject FormControlModel
   * @returns
   */
  createFormFieldControl(fieldObject: FormControlModel) {
    const fieldValidation: ValidatorFn[] = [];
    if (fieldObject['validators']) {
      if (fieldObject['validators']['required']) {
        fieldValidation.push(Validators.required);
      }
      if (fieldObject['validators']['email']) {
        fieldValidation.push(emailValidator);
      }
      if (fieldObject['validators']['max']) {
        fieldValidation.push(Validators.max(fieldObject['validators']['max']));
      }
      if (fieldObject['validators']['min']) {
        fieldValidation.push(Validators.min(fieldObject['validators']['min']));
      }
      if (fieldObject['validators']['pattern']) {
        fieldValidation.push(Validators.pattern(fieldObject['validators']['pattern']));
      }
      if (fieldObject['validators']['minLength']) {
        fieldValidation.push(Validators.minLength(fieldObject['validators']['minLength']));
      }
      if (fieldObject['validators']['maxLength']) {
        fieldValidation.push(Validators.maxLength(fieldObject['validators']['maxLength']));
      }
    }
    let fieldPatchValue = '';
    if (Object.prototype.hasOwnProperty.call(fieldObject, 'value')) {
      fieldPatchValue = fieldObject['value'];
    }
    return new FormControl(fieldPatchValue, fieldValidation as ValidatorFn[]);
  }

  /**
   * Create checkbox array
   * @param formArray
   * @param buildFormArray
   * @returns
   */
  buildCheckboxArray(formArray: FormControlModel, buildFormArray?: AnyType[]): FormArray<FormControl<AnyType>> {
    const arr =
      formArray?.options?.map((option) => {
        if (buildFormArray && buildFormArray.length > 0 && buildFormArray.includes(option.id)) {
          option.selected = true;
        }
        return new FormControl(option.selected ? option.value : null);
      }) || [];
    return new FormArray(arr);
  }

  /**
   * Validate the length of an input field
   * Returns true if valid, otherwise false
   */
  validateLength(minLength = 0, maxLength = 0, value = '') {
    // validate min length
    if (minLength > 0 && value.length < minLength) {
      return false;
    }
    // validate max length
    if (maxLength > 0 && value.length > maxLength) {
      return false;
    }
    return true;
  }

  updateFormFieldVisibility(step: StepperGroupModel, formFieldName: string, formFieldVisibility: Record<string, boolean[]>) {
    const formControl = step.formFields?.find((formControl) => formControl.formFieldName === formFieldName);
    if (formControl) {
      const formFields = formControl.formFieldGroup || [];
      for (const field of formFields) {
        formFieldVisibility[field.formFieldName] = [field.visible];
      }
    }
  }

  /**
   * Create the form groups for each step
   * @param steps
   * @returns
   */
  createStepperFormGroups(steps: StepperGroupModel[], formFieldVisibility: Record<string, boolean[]>) {
    const stepperFormGroups = new FormGroup({});
    let tagsArray: string[] = [];
    steps.forEach((step) => {
      const [formGroup, tags] = this.getFormGroupFor(step, formFieldVisibility);
      // assuming only 1 input for tags/keywords in the whole stepper dialog
      tagsArray = tags.length ? tags : tags;
      stepperFormGroups.addControl(step.stepperGroupName, formGroup);
    });

    return { stepperFormGroups, tagsArray };
  }

  buildBuyoutRightSecondLevelFields(
    event: MatSelectChange,
    formGroupField: AnyType,
    stepperFormGroups: FormGroup<AnyType>,
    availableTitles: string[],
    disableBuyoutRightsTitle = false,
    formFieldVisibility?: Record<string, boolean[]>,
    isLufthansaGroupClient?: boolean,
  ) {
    const selectedValue: string = event.value._id;
    const isSecondLvl: boolean = event.value.secondLvl || false;

    formGroupField.formFields = [];
    let buyoutRightFields: FormControlModel[] = [];

    formGroupField.formFields.push(buyoutRightsFieldMakeOS(availableTitles, disableBuyoutRightsTitle));

    if (isSecondLvl) {
      this.secondLevelLabel = BuyoutRightsMakeOS.find((right) => right._id === selectedValue)?.name || '';
      this.secondLevelLabel = this.secondLevelLabel === 'Creators' ? 'creator' : this.secondLevelLabel;
      this.secondLevelOptions = BuyoutRightsMakeOSSecondLevel.filter((right) => right.firstLvl === selectedValue);
      buyoutRightFields = buyoutRightsSecondLevelField(this.secondLevelLabel, this.secondLevelOptions);
    } else {
      buyoutRightFields = buyoutRightsFieldsMakeOS(isLufthansaGroupClient).filter((list) => list.linkableArray?.includes(selectedValue));
    }

    formGroupField.formFields.push(...buyoutRightFields);

    const formFieldsControls: UntypedFormGroup = this.buildFormGroup(formGroupField.formFields, formFieldVisibility);

    stepperFormGroups.removeControl('usageRightsGeneralInformation');
    stepperFormGroups.addControl('usageRightsGeneralInformation', formFieldsControls);
  }

  buildBuyoutRightSecondLevelMakeOsFields(
    selectedValue: string,
    formGroupField: StepperGroupModel,
    stepperFormGroups: FormGroup<AnyType>,
    availableTitles: string[],
    disableBuyoutRightsTitle = false,
    formFieldVisibility?: Record<string, boolean[]>,
    isLufthansaGroupClient?: boolean,
  ) {
    this.updateFormGroupField(formGroupField, stepperFormGroups, availableTitles, disableBuyoutRightsTitle, selectedValue, isLufthansaGroupClient);

    const formFieldsControls = this.buildFormGroup(formGroupField.formFields, formFieldVisibility);

    this.markBuyoutRightsTitleAsDirty(formFieldsControls);

    this.getFormGroupArray(selectedValue, formFieldsControls.controls['buyoutRightsFormArray'] as UntypedFormArray, 0, isLufthansaGroupClient);

    stepperFormGroups.removeControl('usageRightsGeneralInformation');
    stepperFormGroups.addControl('usageRightsGeneralInformation', formFieldsControls);
  }

  updateFormGroupField(
    formGroupField: StepperGroupModel,
    stepperFormGroups: FormGroup<AnyType>,
    availableTitles: string[],
    disableBuyoutRightsTitle: boolean,
    selectedValue: string,
    isLufthansaGroupClient?: boolean,
  ) {
    formGroupField.formFields = [];
    let buyoutRightFields: FormControlModel[] = [];
    const buyoutRightsTitleValue = stepperFormGroups.value?.usageRightsGeneralInformation?.buyoutRightsTitle;
    formGroupField.formFields.push(buyoutRightsFieldMakeOS(availableTitles, disableBuyoutRightsTitle, buyoutRightsTitleValue));

    buyoutRightFields = this.secondLevelBuyoutRightsFields(selectedValue, isLufthansaGroupClient);

    formGroupField.formFields.push(...buyoutRightFields);
  }

  buildBuyoutRightFields(
    event: MatSelectChange,
    formGroupField: AnyType,
    stepperFormGroups: FormGroup<AnyType>,
    availableTitles: string[],
    disableBuyoutRightsTitle = false,
  ) {
    const selectedValue: string = event.value._id;
    formGroupField.formFields = [];
    let buyoutRightFields: FormControlModel[] = [];

    if (CustomClients.SEAT === this.brandingKey) {
      buyoutRightFields = buyoutRightsFieldsSeat().filter((list) => list.linkableArray?.includes(selectedValue));
      formGroupField.formFields.push(buyoutRightsFieldSeat(availableTitles, disableBuyoutRightsTitle));
    } else {
      buyoutRightFields = buyoutRightsFields(this.brandingKey).filter((list) => list.linkableArray?.includes(selectedValue));
      formGroupField.formFields.push(buyoutRightsField(availableTitles, disableBuyoutRightsTitle));
    }

    formGroupField.formFields.push(...buyoutRightFields);

    const formFieldsControls = this.buildFormGroup(formGroupField.formFields);

    stepperFormGroups.removeControl('usageRightsGeneralInformation');
    stepperFormGroups.addControl('usageRightsGeneralInformation', formFieldsControls);
  }

  secondLevelBuyoutRightsFields(selectedValue: string, isLufthansaGroupClient?: boolean) {
    const selectedLinkableFields = this.updateFormFieldsVisibleProp(selectedValue, isLufthansaGroupClient);
    return [...selectedLinkableFields, ...buyoutRightsSecondLevelField(this.secondLevelLabel, this.secondLevelOptions)];
  }

  updateFormFieldsVisibleProp(selectedValue: string, isLufthansaGroupClient?: boolean): FormControlModel[] {
    return buyoutRightsFieldsMakeOS(isLufthansaGroupClient)
      .filter((list) => list.linkableArray?.includes(selectedValue))
      .map((list) => {
        list.formFieldGroup?.forEach((formField) => {
          formField.visible = formField.linkableArray?.includes(selectedValue) ?? false;
        });
        return list;
      });
  }

  getFormGroupArray(selectedValue: string, buyoutRightsFormArray: UntypedFormArray, formArrayIndex = 0, isLufthansaGroupClient?: boolean) {
    const excludedFields = ['territoriesFormArray', 'date-label'];
    const formGroupArray = buyoutRightsFieldsMakeOS(isLufthansaGroupClient)
      .filter((field) => field.linkableArray?.includes(selectedValue))
      .flatMap((field) => field.formFieldGroup || [])
      .filter((field) => !excludedFields.includes(field?.formFieldName) && !field?.linkableArray?.includes(selectedValue));

    const formGroup = buyoutRightsFormArray.at(formArrayIndex);
    formGroupArray.forEach(({ formFieldName }) => {
      this.removeRequiredValidator(formFieldName, formGroup);
    });
  }

  removeRequiredValidator(formFieldName: string, formGroup: AbstractControl) {
    const control = formGroup.get(formFieldName);
    if (control) {
      control.removeValidators([Validators.required]);
      control.updateValueAndValidity();
    }
  }

  markBuyoutRightsTitleAsDirty(formFieldsControls: FormGroup) {
    const buyoutRightsTitleControl = formFieldsControls.get('buyoutRightsTitle');
    if (buyoutRightsTitleControl) {
      buyoutRightsTitleControl.markAsDirty();
      buyoutRightsTitleControl.markAsTouched();
      buyoutRightsTitleControl.updateValueAndValidity();
    }
  }
}
